import React, { useState, useEffect } from 'react';
import { Card, Form, Input, InputNumber, Select, TimePicker, Button, message, Space, Tag, Table, Modal, Popconfirm, ColorPicker, Switch, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { employeeShiftsApi, show422Error, ValidationError } from '../../../utils/api';
import { scrollToFirstError } from '../../../utils/formHelpers';
import { BulkSelect } from '../../common/bulkSelectProps';
import { buildEmployeeShiftPayload, getShiftFormFieldsToValidate } from '../../../utils/shiftPayloadUtils';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import moment from 'moment-jalaali';
import 'moment-jalaali';

const deriveShiftShortCode = (name, existing) => {
  if (existing?.trim()) return existing.trim();
  if (!name?.trim()) return 'SH';
  const ascii = name.trim().replace(/\s+/g, '-').slice(0, 12);
  return ascii || 'SH';
};

const { Option } = Select;

const ShiftSettingsTab = ({ data, onUpdate }) => {
  const { canEdit, canDelete } = useResourcePermissions('shift');
  const [shiftForm] = Form.useForm();
  const [shifts, setShifts] = useState([]);
  const [defaultShiftId, setDefaultShiftId] = useState(null);
  const [shiftModalVisible, setShiftModalVisible] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [shiftLoading, setShiftLoading] = useState(false);
  const [shiftType, setShiftType] = useState('strict');

  useEffect(() => {
    if (data) {
      const shiftsList = data.employee_shifts || [];
      setShifts(shiftsList);

       const settings = data.attendance_setting || {};
       if (settings.default_employee_shift) {
         setDefaultShiftId(settings.default_employee_shift);
       }
    }
  }, [data]);

  const weekDays = [
    { label: 'شنبه', value: '6' },
    { label: 'یکشنبه', value: '0' },
    { label: 'دوشنبه', value: '1' },
    { label: 'سه‌شنبه', value: '2' },
    { label: 'چهارشنبه', value: '3' },
    { label: 'پنج‌شنبه', value: '4' },
    { label: 'جمعه', value: '5' },
  ];

  const handleAddShift = () => {
    setEditingShift(null);
    setShiftType('strict');
    shiftForm.resetFields();
    shiftForm.setFieldsValue({
      shift_type: 'strict',
      shift_short_code: '',
      color: '#16813D',
      office_open_days: ['0', '1', '2', '3', '4'],
      auto_clock_out_time: 1,
      early_clock_in: 15,
      late_mark_duration: 10,
      clockin_in_day: 2,
    });
    setShiftModalVisible(true);
  };

  const handleEditShift = (record) => {
    setEditingShift(record);
    setShiftType(record.shift_type || 'strict');
    
    // Parse office_open_days
    let officeOpenDays = [];
    try {
      if (record.office_open_days) {
        officeOpenDays = typeof record.office_open_days === 'string' 
          ? JSON.parse(record.office_open_days) 
          : record.office_open_days;
      }
      officeOpenDays = (officeOpenDays || []).map((d) => String(d));
    } catch (e) {
      console.error('Error parsing office_open_days:', e);
    }

    shiftForm.setFieldsValue({
      shift_type: record.shift_type || 'strict',
      shift_name: record.shift_name,
      shift_short_code: record.shift_short_code,
      color: record.color || '#16813D',
      office_start_time: record.office_start_time ? moment(record.office_start_time, 'HH:mm:ss') : null,
      office_end_time: record.office_end_time ? moment(record.office_end_time, 'HH:mm:ss') : null,
      auto_clock_out_time: record.auto_clock_out_time || 1,
      halfday_mark_time: record.halfday_mark_time ? moment(record.halfday_mark_time, 'HH:mm:ss') : null,
      early_clock_in: record.early_clock_in || 0,
      late_mark_duration: record.late_mark_duration || 0,
      clockin_in_day: record.clockin_in_day || 1,
      office_open_days: officeOpenDays,
      total_shift_hours: record.flexible_total_hours || null,
      halfday_shift_hours: record.flexible_half_day_hours || null,
    });
    setShiftModalVisible(true);
  };

  const handleDeleteShift = async (id) => {
    try {
      await employeeShiftsApi.delete(id);
      message.success('شیفت با موفقیت حذف شد');
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('خطا در حذف شیفت:', error);
      message.error(error?.message || 'خطا در حذف شیفت');
    }
  };

  const handleSetDefaultShift = async (id) => {
    try {
      await employeeShiftsApi.setDefault(id);
      message.success('شیفت پیش‌فرض تنظیم شد');
      setDefaultShiftId(id);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('خطا در تنظیم شیفت پیش‌فرض:', error);
      message.error(error?.message || 'خطا در تنظیم شیفت پیش‌فرض');
    }
  };

  const handleShiftSubmit = async () => {
    try {
      const shiftTypeValue = shiftForm.getFieldValue('shift_type') || shiftType;
      const values = await shiftForm.validateFields(getShiftFormFieldsToValidate(shiftTypeValue));
      setShiftLoading(true);

      const payload = buildEmployeeShiftPayload(values, {
        deriveShortCode: deriveShiftShortCode,
      });

      if (editingShift) {
        await employeeShiftsApi.update(editingShift.id, payload);
        message.success('شیفت با موفقیت ویرایش شد');
      } else {
        await employeeShiftsApi.create(payload);
        message.success('شیفت با موفقیت ایجاد شد');
      }

      setShiftModalVisible(false);
      setEditingShift(null);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      if (error.errorFields && error.errorFields.length > 0) {
        scrollToFirstError(shiftForm);
      } else if (error instanceof ValidationError || (error.error && error.error.details)) {
        show422Error(error, shiftForm);
      } else {
        message.error(error?.message || 'خطا در ذخیره شیفت');
      }
    } finally {
      setShiftLoading(false);
    }
  };

  const shiftColumns = [
    {
      title: 'نام شیفت',
      dataIndex: 'shift_name',
      key: 'shift_name',
      render: (text, record) => (
        <div className="flex items-center gap-2">
          {record.color && (
            <div
              className="w-4 h-4 rounded"
              style={{ backgroundColor: record.color }}
            />
          )}
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'کد',
      dataIndex: 'shift_short_code',
      key: 'shift_short_code',
    },
    {
      title: 'ساعت شروع',
      dataIndex: 'office_start_time',
      key: 'office_start_time',
      render: (text) => text ? text.substring(0, 5) : '-',
    },
    {
      title: 'ساعت پایان',
      dataIndex: 'office_end_time',
      key: 'office_end_time',
      render: (text) => text ? text.substring(0, 5) : '-',
    },
    {
      title: 'نوع',
      dataIndex: 'shift_type',
      key: 'shift_type',
      render: (text) => (
        <Tag color={text === 'strict' ? 'blue' : 'green'}>
          {text === 'strict' ? 'سخت' : 'انعطاف‌پذیر'}
        </Tag>
      ),
    },
    {
      title: 'شیفت پیش‌فرض',
      key: 'default',
      align: 'center',
      render: (_, record) => (
        <Switch
          size="small"
          checked={record.id === defaultShiftId}
          checkedChildren="بله"
          unCheckedChildren="خیر"
          onChange={(checked) => {
            // فقط اجازه روشن کردن داریم؛ خاموش کردن را نادیده می‌گیریم
            if (!checked) return;
            handleSetDefaultShift(record.id);
          }}
        />
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {canEdit ? (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditShift(record)}
              size="small"
              className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
              style={{ fontFamily: 'CustomFont' }}
              title="ویرایش"
            />
          ) : null}
          {canDelete && record.id !== defaultShiftId ? (
            <Popconfirm
              title="آیا مطمئن هستید؟"
              onConfirm={() => handleDeleteShift(record.id)}
              okText="بله"
              cancelText="خیر"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
                className="hover:bg-red-50 dark:hover:bg-red-900/20"
                style={{ fontFamily: 'CustomFont' }}
                title="حذف"
              />
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* لیست شیفت‌ها */}
      <Card className="bg-white dark:bg-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ fontFamily: 'CustomFont' }}>
            لیست شیفت‌های کاری
          </h3>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddShift}
            className="bg-green-600 hover:bg-green-700 border-0"
            style={{ fontFamily: 'CustomFont' }}
          >
            افزودن شیفت جدید
          </Button>
        </div>
        <Table
          columns={shiftColumns}
          dataSource={shifts}
          rowKey="id"
          pagination={false}
          style={{ fontFamily: 'CustomFont' }}
        />
      </Card>

      {/* Modal ایجاد/ویرایش شیفت */}
      <Modal
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            {editingShift ? 'ویرایش شیفت' : 'افزودن شیفت جدید'}
          </span>
        }
        open={shiftModalVisible}
        onCancel={() => {
          setShiftModalVisible(false);
          setEditingShift(null);
          shiftForm.resetFields();
        }}
        footer={null}
        width={700}
        style={{ fontFamily: 'CustomFont' }}
      >
        <Form
          form={shiftForm}
          layout="vertical"
          onFinish={handleShiftSubmit}
          style={{ fontFamily: 'CustomFont' }}
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>نام شیفت</span>}
              name="shift_name"
              rules={[{ required: true, message: 'لطفاً نام شیفت را وارد کنید' }]}
            >
              <Input placeholder="مثال: شیفت صبح" style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>کد کوتاه</span>}
              name="shift_short_code"
              rules={[{ required: true, message: 'لطفاً کد کوتاه را وارد کنید' }]}
            >
              <Input placeholder="مثال: Sh-T" style={{ fontFamily: 'CustomFont' }} />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>نوع شیفت</span>}
              name="shift_type"
              rules={[{ required: true, message: 'لطفاً نوع شیفت را انتخاب کنید' }]}
            >
              <Select
                onChange={(value) => {
                  setShiftType(value);
                  if (value === 'strict') {
                    shiftForm.setFieldsValue({
                      total_shift_hours: undefined,
                      halfday_shift_hours: undefined,
                    });
                  } else if (value === 'flexible') {
                    shiftForm.setFieldsValue({
                      office_start_time: undefined,
                      office_end_time: undefined,
                      halfday_mark_time: undefined,
                      early_clock_in: undefined,
                      late_mark_duration: undefined,
                      clockin_in_day: undefined,
                      auto_clock_out_time: undefined,
                    });
                  }
                }}
              >
                <Option value="strict">سخت</Option>
                <Option value="flexible">انعطاف‌پذیر</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={<span style={{ fontFamily: 'CustomFont' }}>رنگ</span>}
              name="color"
              rules={[{ required: true, message: 'لطفاً رنگ را انتخاب کنید' }]}
            >
              <ColorPicker showText format="hex" />
            </Form.Item>
          </div>

          {shiftType === 'strict' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>زمان شروع شیفت</span>}
                  name="office_start_time"
                  rules={[{ required: true, message: 'لطفاً ساعت شروع را وارد کنید' }]}
                >
                  <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>زمان پایان شیفت</span>}
                  name="office_end_time"
                  rules={[{ required: true, message: 'لطفاً ساعت پایان را وارد کنید' }]}
                >
                  <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>
              </div>

              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>زمان نشانه‌گذاری نیم‌روز</span>}
                name="halfday_mark_time"
                rules={[{ required: true, message: 'لطفاً ساعت نیمه روز را وارد کنید' }]}
              >
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>

              <div className="grid grid-cols-2 gap-4">
                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>ورود زودهنگام (دقیقه)</span>}
                  name="early_clock_in"
                >
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontFamily: 'CustomFont' }}>تاخیر بعد از (دقیقه)</span>}
                  name="late_mark_duration"
                >
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </div>

              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>حداکثر ورود مجاز در یک روز</span>}
                name="clockin_in_day"
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}

          {shiftType === 'flexible' && (
            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>مجموع ساعات شیفت</span>}
                name="total_shift_hours"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                label={<span style={{ fontFamily: 'CustomFont' }}>ساعات شیفت نیم‌روز</span>}
                name="halfday_shift_hours"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </div>
          )}

          <Form.Item
            label={<span style={{ fontFamily: 'CustomFont' }}>روزهای باز بودن</span>}
            name="office_open_days"
          >
            <BulkSelect
              mode="multiple"
              placeholder="انتخاب روزهای باز بودن"
              style={{ width: '100%' }}
              allValues={weekDays.map((d) => d.value)}
            >
                {weekDays.map((day) => (
                <Option key={day.value} value={day.value}>
                    {day.label}
                </Option>
                ))}
            </BulkSelect>
          </Form.Item>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
            <Button
              onClick={() => {
                setShiftModalVisible(false);
                setEditingShift(null);
                shiftForm.resetFields();
              }}
              disabled={shiftLoading}
              style={{ fontFamily: 'CustomFont' }}
            >
              لغو
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={shiftLoading}
              className="bg-green-600 hover:bg-green-700 border-0"
              style={{ fontFamily: 'CustomFont' }}
            >
              {editingShift ? 'ویرایش' : 'ذخیره'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ShiftSettingsTab;

